<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import SettingMenu from "@/components/shared/SettingMenu.vue";
import { BonoAssistant } from "@bono-packages/chat";
import { surfaceThread } from "@/plugins/threads";
import { useCompanyStore } from "@/stores/company";
import SwitchApp from "@/components/shared/SwitchApp.vue";
import { useUserStore } from "@/stores/user";
import { usePermissions } from "@/composables/usePermissions";
import { UserRoles } from "@/types";
import { hasSwitchAppRoles } from "@/utils/userRoleHelper";


const { t } = useI18n();
const companyStore = useCompanyStore();
const user = useUserStore();
const { userRoles, safeUserIsReadOnly } = usePermissions();
const items = computed(() => {
  // DataHub items'ı dinamik olarak oluştur
  const dataHubItems = [];

  // Read-only kullanıcılar Data Entry'yi göremez
  if (!safeUserIsReadOnly.value) {
    dataHubItems.push({
      label: t("nav.DataEntry"),
      icon: 'ri-file-list-3-line',
      route: 'data-entry'
    });
  }

  dataHubItems.push(
    { label: t('nav.DataExplorer'), route: 'data-explorer', icon: 'ri-search-2-line' },
    { label: t('nav.DataReport'), route: 'data-report', icon: 'ri-file-chart-line' }
  );

  const baseItems = [
    {
      label: t("nav.Dashboard"),
      icon: 'ri-dashboard-line',
      route: 'home'
    },
    {
      label: t("nav.Calculation"),
      icon: 'ri-seedling-line',
      route: 'calculator'
    },
    {
      label: t("nav.Analysis"),
      icon: 'ri-pie-chart-line',
      route: 'analysis'
    },
    {
      label: t("nav.Goals"),
      icon: 'ri-focus-3-line',
      route: 'goal'
    },
    {
      label: t("nav.Reports"),
      icon: 'ri-file-chart-line',
      route: 'reports'
    },
    {
      label: t("nav.Decarbonization"),
      icon: 'ri-tree-line',
      route: 'decarbonization'
    },
    {
      label: t("nav.DataHub"),
      icon: 'ri-database-2-line',
      items: [
        { label: t('nav.StructureData'), route: 'structure-data', icon: 'ri-table-line' },
        {
          label: t("nav.DataEntry"),
          icon: 'ri-file-list-3-line',
          route: 'data-entry'
        },
        {
          label: t("nav.FormBuilder"),
          icon: 'ri-file-settings-line',
          route: 'form-builder'
        },
        { label: t('nav.DataExplorer'), route: 'data-explorer', icon: 'ri-search-2-line' },
        { label: t('nav.DataReport'), route: 'data-report', icon: 'ri-file-chart-line' },
        { label: 'Rule engine', route: 'rule-engine', icon: 'ri-shield-keyhole-line' },
      ]
    },
  ];

  if (!companyStore.companyId) {
    return [baseItems[0]];
  }

  return baseItems;
});

const superAdmin = computed(() => {
  // Fallback: Use direct user store access if permission store fails
  const currentUserRoles = userRoles || user.getUserRoles;

  // Guard clause for undefined userRoles
  if (!currentUserRoles || !Array.isArray(currentUserRoles)) {
    return false;
  }

  return hasSwitchAppRoles(currentUserRoles);
});

const isMultipleApps = computed(() => {
  // Fallback: Use direct user store access if permission store fails
  const currentUserRoles = userRoles || user.getUserRoles;

  // Guard clause for undefined userRoles
  if (!currentUserRoles || !Array.isArray(currentUserRoles)) {
    return false;
  }

  const superAdminCheck = hasSwitchAppRoles(currentUserRoles);

  if (superAdminCheck) {return false}

  const hasSupplyChainRole = currentUserRoles.some(role =>
    [UserRoles.SUPPLY_CHAIN_USER, UserRoles.SUPPLY_CHAIN_ADMIN].includes(role.roleName)
  );
  const hasCarbonRole = currentUserRoles.some(role =>
    [UserRoles.CARBON_ACCOUNTING_USER, UserRoles.CARBON_ACCOUNTING_ADMIN, UserRoles.CARBON_ACCOUNTING_READ_ONLY, UserRoles.CARBON_ACCOUNTING_FACILITY_DATA_ENTRY].includes(role.roleName)
  );
  return hasSupplyChainRole && hasCarbonRole;
});

</script>

<template>
  <div class="mx-auto w-full px-8 sm:px-6 lg:px-8 bg-white shadow">
    <Menubar :model="items" class="!border-none">
      <template #start>
        <router-link :to="{name: 'home'}">
          <img
            class="w-auto lg:block mr-4"
            src="../../assets/logo.svg"
            alt="Bono"
          />
        </router-link>
      </template>
      <template #item="{item, props, hasSubmenu, root}">
        <router-link v-if="item.route && !hasSubmenu" 
                     v-slot="{ href, navigate }"
                     :to="{name: item.route}"
                     active-class="navigation-active-class bg-neautrals-900"
                     class="text-neautrals-500 hover:bg-neautrals-900 hover:text-white rounded-md text-sm font-medium font-lufga flex gap-2 items-center"
        >
          <a v-ripple :href="href" v-bind="props.action" @click="navigate">
            <span :class="item.icon" />
            <span>{{ item.label }}</span>
          </a>
        </router-link>
        <a v-else v-ripple v-bind="props.action" class="text-neautrals-500 hover:bg-neautrals-900 hover:text-white rounded-md text-sm font-medium font-lufga flex gap-2 items-center">
          <span :class="item.icon" />
          <span>{{ item.label }}</span>
        </a>
      </template>
      <template #end>
        <div class="flex items-center gap-2">
          <!--
            The assistant that replaced Ask Bono. Same lane as every other surface, so a conversation
            started here is the same kind of thing as one started on a dashboard — which the old page,
            with its own store and its own drawer, could never be.
          -->
          <BonoAssistant
            v-if="companyStore.companyId"
            :thread-for="surfaceThread"
            :title="t('assistant.title')"
            :placeholder="t('assistant.placeholder')"
            :suggestions="[t('assistant.starter1'), t('assistant.starter2'), t('assistant.starter3')]"
            :words="{ general: t('assistant.general'), talkingAbout: t('assistant.talkingAbout'), threads: t('assistant.threads'), newThread: t('assistant.newThread'), newShort: t('assistant.new'), untitled: t('assistant.untitled'), close: t('assistant.close'), none: t('assistant.none'), welcomeTitle: t('assistant.welcomeTitle'), pickUp: t('assistant.pickUp'), allThreads: t('assistant.allThreads'), newPlaceholder: t('assistant.newPlaceholder') }"
          />
          <SettingMenu />
          <SwitchApp v-if="superAdmin || isMultipleApps"
                     :is-multiple-apps="isMultipleApps"
                     :is-super-admin="superAdmin" />
        </div>
      </template>
    </Menubar>
  </div>
</template>